import { Link } from "react-router-dom";
import ContactSection from "../../components/ContactSection";
import Icon, { type IconName } from "../../components/icons";
import MotionMedia from "../../components/MotionMedia";
import PageHero from "../../components/PageHero";
import Reveal from "../../components/Reveal";
import SiteLayout from "../../components/SiteLayout";

const capabilities: Array<{ icon: IconName; title: string; copy: string; items: string[] }> = [
  { icon: "strategy", title: "Product strategy", copy: "Turn a broad opportunity into a focused product plan your team can act on.", items: ["Discovery workshops", "Product roadmaps", "Technical planning"] },
  { icon: "web", title: "Web platforms", copy: "Create a fast, accessible web experience that supports a real business goal.", items: ["Marketing websites", "Customer portals", "SaaS applications"] },
  { icon: "mobile", title: "Mobile products", copy: "Deliver a confident mobile experience across iOS and Android.", items: ["Native applications", "Cross-platform products", "App modernization"] },
  { icon: "automation", title: "AI & automation", copy: "Reduce repetitive work and help your team make better use of operational knowledge.", items: ["Workflow automation", "AI-assisted tools", "Internal operations"] },
  { icon: "cloud", title: "Cloud & integrations", copy: "Connect the systems your business depends on with a secure, scalable foundation.", items: ["API development", "System integrations", "Cloud architecture"] },
  { icon: "support", title: "Product care", copy: "Keep critical software healthy while adapting it to new customer and business needs.", items: ["Performance tuning", "Feature evolution", "Reliability support"] },
];

const ServiceComponent = () => (
  <SiteLayout>
    <PageHero
      eyebrow="Services"
      title={<>From complex idea to <span>clear product.</span></>}
      description="We combine strategy, product design, and engineering so the work stays connected from the first decision through launch and beyond."
    >
      <div className="hero-actions">
        <Link className="button" to="/#contact">Get a project recommendation <Icon name="arrow" /></Link>
        <Link className="button button-secondary" to="/pricing">See engagement options</Link>
      </div>
    </PageHero>

    <section className="light-section capabilities-section">
      <div className="site-container">
        <Reveal className="section-heading-row">
          <div><p className="section-kicker">Capabilities</p><h2>The right disciplines, working as one.</h2></div>
          <p>Bring us a defined brief or an unresolved problem. We will meet you where the work actually is.</p>
        </Reveal>
        <div className="capability-grid">
          {capabilities.map((capability, index) => (
            <Reveal key={capability.title} delay={(index % 3) * 80}>
              <article className="capability-card">
                <span className="card-icon"><Icon name={capability.icon} /></span>
                <h3>{capability.title}</h3>
                <p>{capability.copy}</p>
                <ul>{capability.items.map((item) => <li key={item}><Icon name="check" />{item}</li>)}</ul>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="dark-section service-journey">
      <div className="site-container journey-grid">
        <Reveal className="journey-copy">
          <p className="section-kicker">Built around the outcome</p>
          <h2>Less handoff. More ownership.</h2>
          <p>
            Strategy informs the experience. The experience informs the
            architecture. Real feedback informs what gets built next. Keeping
            those decisions connected is how we protect quality and momentum.
          </p>
        </Reveal>
        <div className="journey-visual-stack">
          <Reveal delay={90} direction="right">
            <MotionMedia
              className="journey-media"
              src="/images/connected-systems.jpg"
              alt="Layered crimson glass systems connected with precise gold lines"
              caption="Every layer connected to the outcome"
              speed={36}
            />
          </Reveal>
          <Reveal delay={150} className="journey-map" direction="scale">
            <div><span>01</span><strong>Define</strong><small>The valuable problem</small></div>
            <i />
            <div><span>02</span><strong>Design</strong><small>The clearest path</small></div>
            <i />
            <div><span>03</span><strong>Deliver</strong><small>The working product</small></div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="light-section fit-section">
      <div className="site-container fit-grid">
        <Reveal><p className="section-kicker">A strong fit</p><h2>CMJ works best when…</h2></Reveal>
        <div className="fit-list">
          {["The outcome matters more than checking a feature list.", "You want a partner who will ask why and offer a point of view.", "Quality, maintainability, and trust matter after launch.", "You value direct access to the people doing the work."].map((item, index) => (
            <Reveal key={item} delay={index * 65} className="fit-row"><Icon name="check" /><p>{item}</p></Reveal>
          ))}
        </div>
      </div>
    </section>

    <ContactSection />
  </SiteLayout>
);

export default ServiceComponent;
